import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import SyncConstants from "../../constants/SyncConstants";
import { set_notification } from "../../actions/Sync";
import { getSlotDate } from "../../libs/SlotUtils";
import { Activities } from "../../constants/ExpenseConstants";
import useAuthentication from "../../hooks/useAuthentication";
import "./QuickAddModal.scss";

const QuickAddModal = ({ isOpen, onClose }) => {
    const [input, setInput] = useState("");
    const [parsedData, setParsedData] = useState({
        command: null, // "buche" or future commands
        duration: null,
        project: null,
        subproject: null,
        description: "",
        activity: null,
        person: null,
        teamMembers: [] // For "mit" keyword - names added to description
    });
    const [currentStep, setCurrentStep] = useState("command"); // command, duration, project, person, description
    const [tabSuggestion, setTabSuggestion] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [expandedCustomers, setExpandedCustomers] = useState(new Set());
    const [expandedProjects, setExpandedProjects] = useState(new Set());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [allProjects, setAllProjects] = useState([]);
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);

    const inputRef = useRef(null);
    const modalRef = useRef(null);
    const UserSettings = useSelector((state) => state.User);
    const Team = useSelector((state) => state.Team);
    const dispatch = useDispatch();
    const Auth = useAuthentication();

    useEffect(() => {
        if (isOpen) {
            loadAllProjects();
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape" && isOpen) {
                onClose();
            }
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [isOpen, onClose]);

    const loadAllProjects = async () => {
        setIsLoadingProjects(true);
        try {
            const response = await axios({
                method: "get",
                auth: Auth.getAuthObject(),
                url: `${SyncConstants.syncBaseURL}expenseProjects/search`,
                params: { q: '', limit: 1000 },
                responseType: 'json'
            });

            if (response.data && response.data.data) {
                setAllProjects(response.data.data);
            }
        } catch (error) {
            console.error("Error loading projects:", error);
        }
        setIsLoadingProjects(false);
    };

    useEffect(() => {
        parseInput(input);
    }, [input, allProjects]);

    const parseInput = (text) => {
        if (!text.trim()) {
            setParsedData({
                duration: null,
                project: null,
                subproject: null,
                description: "",
                activity: null,
                person: null,
                teamMembers: []
            });
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const parsed = {
            duration: parsedData.duration,
            project: parsedData.project,
            subproject: null,
            description: text,
            activity: null,
            person: parsedData.person,
            teamMembers: []
        };

        const durationPatterns = [
            /(\d+[.,]\d+)\s*h/gi,
            /(\d+):(\d+)/g,
            /(\d+)\s*h/gi,
            /(\d+)\s*min/gi
        ];

        let durationMatch = null;
        for (const pattern of durationPatterns) {
            const match = text.match(pattern);
            if (match) {
                durationMatch = match[0];
                break;
            }
        }

        if (durationMatch) {
            parsed.duration = parseDuration(durationMatch);
            parsed.description = text.replace(durationMatch, "").trim();
        }

        // Check for person mentions (für jemand anderes buchen)
        const personPattern = /für\s+(\w+)/gi;
        const personMatch = parsed.description.match(personPattern);
        if (personMatch) {
            const personName = personMatch[0].replace(/für\s+/gi, "").trim();
            const foundPerson = Object.keys(Team).find(username => 
                Team[username].realName.toLowerCase().includes(personName.toLowerCase())
            );
            if (foundPerson) {
                parsed.person = foundPerson;
                parsed.description = parsed.description.replace(personMatch[0], "").trim();
            }
        }

        // Extract project search term and description FIRST (based on keywords: "auf", "in", or ":")
        let projectSearchTerm = "";
        let descriptionWithoutProject = parsed.description;
        
        // Pattern 1: "auf PROJEKTNAME" oder "in PROJEKTNAME"
        const keywordPattern = /(?:auf|in)\s+([^:]+?)(?:\s*:|$)/gi;
        const keywordMatch = parsed.description.match(keywordPattern);
        
        if (keywordMatch) {
            const fullMatch = keywordMatch[0];
            projectSearchTerm = fullMatch.replace(/^(?:auf|in)\s+/i, "").replace(/\s*:?\s*$/, "").trim();
            descriptionWithoutProject = parsed.description.replace(fullMatch, "").trim();
        } else {
            // Pattern 2: "PROJEKTNAME: Beschreibung" (ohne "auf" oder "in")
            const colonPattern = /^(.+?):\s*(.+)$/;
            const colonMatch = parsed.description.match(colonPattern);
            
            if (colonMatch) {
                projectSearchTerm = colonMatch[1].trim();
                descriptionWithoutProject = colonMatch[2].trim();
            }
        }

        // Now check for team members (mit Ralf, Sarah, Karsten) in the PROJECT search term
        const mitPattern = /mit\s+([\w\s,]+?)(?:\s+(?:auf|in)|$)/gi;
        const mitMatch = projectSearchTerm.match(mitPattern);
        if (mitMatch) {
            const fullMatch = mitMatch[0];
            const namesString = fullMatch.replace(/^mit\s+/i, "").replace(/\s*(?:auf|in)?\s*$/, "").trim();
            
            // Split by comma and parse each name
            const namesList = namesString.split(/[,\s]+/).filter(n => n.length > 0);
            const foundMembers = [];
            
            namesList.forEach(name => {
                const foundPerson = Object.keys(Team).find(username => 
                    Team[username].realName.toLowerCase().includes(name.toLowerCase())
                );
                if (foundPerson) {
                    foundMembers.push(foundPerson);
                }
            });
            
            if (foundMembers.length > 0) {
                parsed.teamMembers = foundMembers;
                projectSearchTerm = projectSearchTerm.replace(fullMatch, "").trim();
            }
        }

        // Update description to the part after keywords
        if (descriptionWithoutProject) {
            parsed.description = descriptionWithoutProject;
        }

        // Only search for projects if none is selected yet
        const searchText = (projectSearchTerm || parsed.description).toLowerCase().trim();
        
        if (!parsed.project && searchText.length >= 1 && allProjects.length > 0) {
            // Smart filtering with scoring
            const matchingProjects = allProjects
                .map(p => {
                    const titleLower = p.title.toLowerCase();
                    const fullPathLower = (p.fullPath || p.breadcrumb || '').toLowerCase();
                    const searchLower = searchText;
                    
                    let score = 0;
                    
                    // Exact title match
                    if (titleLower === searchLower) {
                        score += 1000;
                    }
                    // Title starts with search
                    else if (titleLower.startsWith(searchLower)) {
                        score += 500;
                    }
                    // Title contains search
                    else if (titleLower.includes(searchLower)) {
                        score += 200;
                    }
                    
                    // Full path exact match
                    if (fullPathLower === searchLower) {
                        score += 800;
                    }
                    // Full path contains search
                    else if (fullPathLower.includes(searchLower)) {
                        score += 150;
                    }
                    
                    // Word-by-word matching with position bonus
                    const searchWords = searchLower.split(/\s+/).filter(w => w.length > 0);
                    const titleWords = titleLower.split(/\s+/);
                    const pathWords = fullPathLower.split(/[\s>]+/);
                    
                    searchWords.forEach((searchWord, idx) => {
                        // Bonus for matching words in order
                        titleWords.forEach((titleWord, titleIdx) => {
                            if (titleWord === searchWord) {
                                score += 100;
                                if (titleIdx === idx) score += 50; // Position match bonus
                            } else if (titleWord.startsWith(searchWord)) {
                                score += 50;
                            } else if (titleWord.includes(searchWord)) {
                                score += 25;
                            }
                        });
                        
                        pathWords.forEach(pathWord => {
                            if (pathWord === searchWord) {
                                score += 30;
                            } else if (pathWord.includes(searchWord)) {
                                score += 15;
                            }
                        });
                    });
                    
                    // Multi-word phrase matching in path
                    if (searchWords.length > 1) {
                        const searchPhrase = searchWords.join(' ');
                        if (fullPathLower.includes(searchPhrase)) {
                            score += 300; // Big bonus for phrase match
                        }
                    }
                    
                    return { ...p, score };
                })
                .filter(p => p.score > 0)
                .sort((a, b) => b.score - a.score)
                .slice(0, 10);

            if (matchingProjects.length > 0) {
                console.log('Matching projects:', matchingProjects);
                
                // Separate customers from other projects
                const customers = matchingProjects.filter(p => p.project_type === 'customer');
                const nonCustomerProjects = matchingProjects.filter(p => p.project_type !== 'customer' && p.bookable == 1);
                
                console.log('Customers found:', customers.length);
                console.log('Non-customer projects (bookable only):', nonCustomerProjects.length);
                
                // Build hierarchical structure
                const hierarchicalSuggestions = {
                    customers,
                    nonCustomerProjects
                };
                
                setSuggestions(hierarchicalSuggestions);
                setShowSuggestions(true);
                setActiveSuggestionIndex(0);
            } else {
                setSuggestions({ customers: [], nonCustomerProjects: [] });
                setShowSuggestions(false);
            }
        } else {
            setSuggestions({ customers: [], nonCustomerProjects: [] });
            setShowSuggestions(false);
        }

        setParsedData(parsed);
    };

    const toggleCustomer = (customerId) => {
        console.log('Toggle customer:', customerId);
        setExpandedCustomers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(customerId)) {
                newSet.delete(customerId);
                console.log('Collapsed customer:', customerId);
            } else {
                newSet.add(customerId);
                console.log('Expanded customer:', customerId);
            }
            console.log('Expanded customers:', Array.from(newSet));
            return newSet;
        });
    };

    const toggleProject = (projectId) => {
        setExpandedProjects(prev => {
            const newSet = new Set(prev);
            if (newSet.has(projectId)) {
                newSet.delete(projectId);
            } else {
                newSet.add(projectId);
            }
            return newSet;
        });
    };

    // Helper: Alle Kinder eines Kunden flach sammeln (für Keyboard-Navigation)
    const getAllChildren = (customerId) => {
        console.log('getAllChildren called for customer:', customerId);
        
        // Get direct children of customer
        const getChildren = (parentId) => {
            return allProjects.filter(p => p.parent == parentId);
        };
        
        const flatten = (projects) => {
            let result = [];
            projects.forEach(p => {
                console.log('Processing project:', p.title, 'bookable:', p.bookable);
                const children = getChildren(p.id);
                const hasChildren = children.length > 0;
                
                // Füge Projekt hinzu wenn buchbar oder hat Kinder
                if (p.bookable == 1 || hasChildren) {
                    result.push(p);
                    console.log('Added project:', p.title);
                }
                // Wenn expandiert, füge Kinder auch hinzu
                if (expandedProjects.has(p.id) && hasChildren) {
                    console.log('Project is expanded, adding children of:', p.title);
                    result = result.concat(flatten(children));
                }
            });
            return result;
        };
        
        const directChildren = getChildren(customerId);
        console.log('Direct children of customer:', directChildren.map(c => c.title));
        const result = flatten(directChildren);
        console.log('Final result:', result.map(c => c.title));
        return result;
    };

    const parseDuration = (durationStr) => {
        let hours = 0;
        let minutes = 0;

        if (durationStr.includes(":")) {
            [hours, minutes] = durationStr.split(":").map(Number);
        } else {
            const decimalMatch = durationStr.match(/(\d+[.,]\d+)/);
            if (decimalMatch) {
                const decimal = parseFloat(decimalMatch[1].replace(",", "."));
                hours = Math.floor(decimal);
                minutes = Math.round((decimal - hours) * 60);
            } else {
                const hoursMatch = durationStr.match(/(\d+)\s*h/i);
                if (hoursMatch) {
                    hours = parseInt(hoursMatch[1]);
                    minutes = 0;
                } else {
                    const minutesMatch = durationStr.match(/(\d+)\s*min/i);
                    if (minutesMatch) {
                        const totalMinutes = parseInt(minutesMatch[1]);
                        hours = Math.floor(totalMinutes / 60);
                        minutes = totalMinutes % 60;
                    }
                }
            }
        }

        // Round up to next 15 minutes (0, 15, 30, 45)
        const totalMinutes = hours * 60 + minutes;
        const roundedMinutes = Math.ceil(totalMinutes / 15) * 15;
        
        return { 
            hours: Math.floor(roundedMinutes / 60), 
            minutes: roundedMinutes % 60 
        };
    };

    const selectProject = (project) => {
        setParsedData(prev => ({ 
            ...prev, 
            project,
            description: prev.description // Keep the current description
        }));
        setShowSuggestions(false);
        
        // Don't modify the input - let user continue typing
        inputRef.current?.focus();
    };

    const removeToken = (tokenType) => {
        setParsedData(prev => ({ 
            ...prev, 
            [tokenType]: tokenType === 'teamMembers' ? [] : null 
        }));
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions) {
            // ESC schließt das Modal wenn keine Suggestions offen sind
            if (e.key === 'Escape') {
                onClose();
                return;
            }
            return;
        }

        const expandedCustomer = suggestions.customers?.find(c => expandedCustomers.has(c.id));

        // Zahlen 1-9 für schnelle Auswahl
        if (e.key >= '1' && e.key <= '9') {
            e.preventDefault();
            const index = parseInt(e.key) - 1;
            
            if (expandedCustomer) {
                // Wenn ein Kunde expandiert ist, wähle Unterprojekt aus
                const allChildren = getAllChildren(expandedCustomer.id);
                console.log('All visible children:', allChildren.map(c => c.title));
                if (allChildren[index]) {
                    const project = allChildren[index];
                    const hasChildren = allProjects.filter(p => p.parent == project.id).length > 0;
                    console.log('Selected project:', project.title, 'bookable:', project.bookable, 'hasChildren:', hasChildren);
                    if (project.bookable == 1) {
                        selectProject(project);
                    } else if (hasChildren) {
                        toggleProject(project.id);
                    }
                }
            } else {
                // Im Suchmodus: Kunden oder Projekte
                if (suggestions.customers && suggestions.customers[index]) {
                    toggleCustomer(suggestions.customers[index].id);
                } else {
                    // Projekte nach Kunden
                    const customerCount = suggestions.customers?.length || 0;
                    const projectIndex = index - customerCount;
                    if (suggestions.nonCustomerProjects && suggestions.nonCustomerProjects[projectIndex]) {
                        const project = suggestions.nonCustomerProjects[projectIndex];
                        if (project.bookable == 1) {
                            selectProject(project);
                        }
                    }
                }
            }
            return;
        }

        switch (e.key) {
            case "ArrowDown":
            case "ArrowUp":
                // Native scrolling works in expanded mode
                e.preventDefault();
                break;
            case "ArrowRight":
                e.preventDefault();
                if (!expandedCustomer && suggestions.customers && suggestions.customers.length > 0) {
                    // Expand first customer
                    toggleCustomer(suggestions.customers[0].id);
                }
                break;
            case "ArrowLeft":
                e.preventDefault();
                if (expandedCustomer) {
                    // Collapse customer
                    toggleCustomer(expandedCustomer.id);
                }
                break;
            case "Enter":
                e.preventDefault();
                if (!expandedCustomer && suggestions.customers && suggestions.customers.length > 0) {
                    // Expand first customer with Enter
                    toggleCustomer(suggestions.customers[0].id);
                }
                break;
            case "Escape":
                e.preventDefault();
                if (expandedCustomer) {
                    // Eine Ebene zurück - collapse customer
                    toggleCustomer(expandedCustomer.id);
                } else {
                    // Dropdown schließen
                    setShowSuggestions(false);
                    setExpandedCustomers(new Set());
                    setExpandedProjects(new Set());
                }
                break;
        }
    };

    const canSubmit = () => {
        return parsedData.duration && 
               parsedData.project && 
               parsedData.description.trim() !== "" &&
               !isSubmitting &&
               // Check if description has content after keywords (requires ":")
               hasValidDescription();
    };

    const hasValidDescription = () => {
        const desc = parsedData.description.trim();
        // Description is valid if it's not empty and doesn't look like it's still waiting for ":"
        return desc.length > 0 && !desc.match(/^\s*$/);
    };

    const getMultipliedDuration = () => {
        if (!parsedData.duration) return null;
        const multiplier = parsedData.teamMembers.length > 0 ? parsedData.teamMembers.length + 1 : 1;
        const totalMinutes = (parsedData.duration.hours * 60 + parsedData.duration.minutes) * multiplier;
        return {
            hours: Math.floor(totalMinutes / 60),
            minutes: totalMinutes % 60,
            multiplier
        };
    };

    const getFinalDescription = () => {
        const desc = parsedData.description.trim();
        
        // Check if we need a description after keywords
        if (!desc || desc.length === 0) {
            return null; // No description yet
        }
        
        if (parsedData.teamMembers.length === 0) return desc;
        
        const names = parsedData.teamMembers.map(username => Team[username]?.realName).join(", ");
        return `${desc} (mit ${names})`;
    };

    const handleSubmit = async () => {
        if (!canSubmit()) return;

        setIsSubmitting(true);

        const multipliedDuration = getMultipliedDuration();
        const finalDescription = getFinalDescription();

        const requestData = {
            title: finalDescription,
            durationHours: multipliedDuration.hours,
            durationMinutes: multipliedDuration.minutes,
            expenseProjectId: parsedData.project.id,
            person: parsedData.person || UserSettings.userName,
            slotId: null,
            expenseDate: getSlotDate(new Date()),
            entryDate: getSlotDate(new Date()),
            activity: parsedData.activity || Team[UserSettings.userName]?.defaultActivity || "Sonstige"
        };

        if (parsedData.person && parsedData.person !== UserSettings.userName) {
            requestData.bookedBy = UserSettings.userName;
        }

        try {
            await axios({
                method: "post",
                auth: Auth.getAuthObject(),
                url: SyncConstants.syncBaseURL + "expenseEntry",
                responseType: 'json',
                data: requestData
            });

            dispatch(set_notification("Aufwand erfolgreich gebucht! 🎉", "success"));
            
            setInput("");
            setParsedData({
                duration: null,
                project: null,
                subproject: null,
                description: "",
                activity: null,
                person: null,
                teamMembers: []
            });
            
            onClose();
        } catch (error) {
            console.error("Error submitting expense:", error);
            dispatch(set_notification("Fehler beim Buchen des Aufwands", "error"));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="quick-add-overlay" onClick={onClose}>
            <div className="quick-add-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
                <div className="quick-add-header">
                    <h5 className="mb-0">
                        Schnelleingabe
                    </h5>
                    <button className="btn-close-quick" onClick={onClose}>
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                <div className="quick-add-body">
                    <div className="quick-input-wrapper">
                        <input
                            ref={inputRef}
                            type="text"
                            className="quick-input"
                            placeholder='z.B. "2h mit Ralf, Sarah auf Stadtwerke: Team-Call"'
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isSubmitting}
                        />
                        
                        {isLoadingProjects && (
                            <div className="loading-indicator">
                                <span className="spinner-border spinner-border-sm"></span>
                            </div>
                        )}
                    </div>

                    <div className="parsed-tokens">
                        {parsedData.duration && (
                            <span className="token token-duration">
                                <i className="bi bi-clock me-1"></i>
                                {parsedData.duration.hours}h {parsedData.duration.minutes}min
                                {parsedData.teamMembers.length > 0 && (
                                    <span className="ms-1">× {parsedData.teamMembers.length + 1}</span>
                                )}
                                <button 
                                    className="token-remove" 
                                    onClick={() => removeToken("duration")}
                                >
                                    <i className="bi bi-x"></i>
                                </button>
                            </span>
                        )}
                        
                        {parsedData.project && (
                            <span className="token token-project">
                                <i className="bi bi-folder me-1"></i>
                                {parsedData.project.title}
                                <button 
                                    className="token-remove" 
                                    onClick={() => removeToken("project")}
                                >
                                    <i className="bi bi-x"></i>
                                </button>
                            </span>
                        )}
                        
                        {parsedData.person && parsedData.person !== UserSettings.userName && (
                            <span className="token token-person">
                                <i className="bi bi-person me-1"></i>
                                {Team[parsedData.person]?.realName}
                                <button 
                                    className="token-remove" 
                                    onClick={() => removeToken("person")}
                                >
                                    <i className="bi bi-x"></i>
                                </button>
                            </span>
                        )}
                        
                        {parsedData.teamMembers.length > 0 && (
                            <span className="token token-team">
                                <i className="bi bi-people me-1"></i>
                                mit {parsedData.teamMembers.map(u => Team[u]?.realName).join(", ")}
                                <button 
                                    className="token-remove" 
                                    onClick={() => removeToken("teamMembers")}
                                >
                                    <i className="bi bi-x"></i>
                                </button>
                            </span>
                        )}
                    </div>

                    {showSuggestions && (suggestions.customers?.length > 0 || suggestions.nonCustomerProjects?.length > 0) && (
                        <div className="suggestions-dropdown">
                            {/* Check if any customer is expanded */}
                            {(() => {
                                const expandedCustomer = suggestions.customers?.find(c => expandedCustomers.has(c.id));
                                
                                if (expandedCustomer) {
                                    // EXPANDED MODE: Show customer with ALL its children hierarchically
                                    
                                    // Helper: Get all children of a parent ID
                                    const getChildren = (parentId) => {
                                        return allProjects.filter(p => p.parent == parentId);
                                    };
                                    
                                    // Recursive render function with index tracking
                                    let visibleItemCounter = 0;
                                    const renderProjectTree = (project, depth = 1) => {
                                        const children = getChildren(project.id);
                                        const hasChildren = children.length > 0;
                                        const isExpanded = expandedProjects.has(project.id);
                                        const isBookable = project.bookable == 1;
                                        const shouldShowShortcut = (isBookable || hasChildren);
                                        const myIndex = shouldShowShortcut ? visibleItemCounter++ : -1;
                                        
                                        return (
                                            <React.Fragment key={project.id}>
                                                <div
                                                    className={`suggestion-item suggestion-child ${!isBookable ? 'suggestion-disabled' : ''}`}
                                                    style={{ paddingLeft: `${2.5 + (depth * 1.5)}rem` }}
                                                    onClick={() => {
                                                        if (hasChildren) {
                                                            toggleProject(project.id);
                                                        } else if (isBookable) {
                                                            selectProject(project);
                                                        }
                                                    }}
                                                >
                                                    <div className="suggestion-main">
                                                        <div className="suggestion-icon">
                                                            {hasChildren ? (
                                                                <i className="bi bi-folder-fill"></i>
                                                            ) : (
                                                                <i className="bi bi-file-earmark"></i>
                                                            )}
                                                        </div>
                                                        <div className="suggestion-content">
                                                            <div className="suggestion-title">
                                                                {project.title}
                                                                {!isBookable && !hasChildren && (
                                                                    <span className="text-muted ms-2" style={{ fontSize: '0.75rem' }}>(nicht buchbar)</span>
                                                                )}
                                                            </div>
                                                            {hasChildren ? (
                                                                <div className="suggestion-meta">
                                                                    {children.length} Unterprojekt(e)
                                                                </div>
                                                            ) : !isBookable && (
                                                                <div className="suggestion-meta text-muted">
                                                                    Nicht buchbar
                                                                </div>
                                                            )}
                                                        </div>
                                                        {shouldShowShortcut && myIndex >= 0 && myIndex < 9 && (
                                                            <div className="suggestion-shortcut">
                                                                {myIndex + 1}
                                                            </div>
                                                        )}
                                                        {hasChildren && (
                                                            <div className="suggestion-expand">
                                                                <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {/* Render children recursively if expanded */}
                                                {isExpanded && hasChildren && children.map(child => renderProjectTree(child, depth + 1))}
                                            </React.Fragment>
                                        );
                                    };
                                    
                                    // Render expanded customer
                                    const customerChildren = getChildren(expandedCustomer.id);
                                    
                                    return (
                                        <React.Fragment>
                                            <div
                                                className="suggestion-item suggestion-customer active"
                                                onClick={() => toggleCustomer(expandedCustomer.id)}
                                            >
                                                <div className="suggestion-main">
                                                    <div className="suggestion-icon">
                                                        <i className="bi bi-building-fill"></i>
                                                    </div>
                                                    <div className="suggestion-content">
                                                        <div className="suggestion-title">
                                                            {expandedCustomer.title}
                                                        </div>
                                                        <div className="suggestion-meta">
                                                            {customerChildren.length} Projekt(e)
                                                        </div>
                                                    </div>
                                                    <div className="suggestion-expand">
                                                        <i className="bi bi-chevron-down"></i>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Render all direct children */}
                                            <div className="suggestion-children">
                                                {customerChildren.map(project => renderProjectTree(project, 1))}
                                            </div>
                                        </React.Fragment>
                                    );
                                }
                                
                                // SEARCH MODE: Show customers and fuzzy-matched projects
                                return (
                                    <React.Fragment>
                                        {/* Customers Section */}
                                        {suggestions.customers?.length > 0 && (
                                            <React.Fragment>
                                                <div className="suggestion-section-header">
                                                    <i className="bi bi-building me-2"></i>
                                                    Kunden
                                                </div>
                                                {suggestions.customers.map((customer, index) => (
                                                    <div
                                                        key={customer.id}
                                                        className="suggestion-item suggestion-customer"
                                                        onClick={() => toggleCustomer(customer.id)}
                                                    >
                                                        <div className="suggestion-main">
                                                            <div className="suggestion-icon">
                                                                <i className="bi bi-building-fill"></i>
                                                            </div>
                                                            <div className="suggestion-content">
                                                                <div className="suggestion-title">
                                                                    {customer.title}
                                                                </div>
                                                            </div>
                                                            <div className="suggestion-shortcut">
                                                                {index + 1}
                                                            </div>
                                                            <div className="suggestion-expand">
                                                                <i className="bi bi-chevron-right"></i>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </React.Fragment>
                                        )}
                                        
                                        {/* Non-customer projects (fuzzy search results) */}
                                        {suggestions.nonCustomerProjects?.length > 0 && (
                                            <React.Fragment>
                                                <div className="suggestion-section-header">
                                                    <i className="bi bi-search me-2"></i>
                                                    Projekte
                                                </div>
                                                {suggestions.nonCustomerProjects.map((project, index) => {
                                                    const isBookable = project.bookable == 1;
                                                    const customerCount = suggestions.customers?.length || 0;
                                                    const shortcutNumber = customerCount + index + 1;
                                                    
                                                    return (
                                                        <div
                                                            key={project.id}
                                                            className={`suggestion-item ${!isBookable ? 'suggestion-disabled' : ''}`}
                                                            onClick={() => {
                                                                if (isBookable) {
                                                                    selectProject(project);
                                                                }
                                                            }}
                                                        >
                                                            <div className="suggestion-main">
                                                                <div className="suggestion-icon">
                                                                    <i className="bi bi-folder"></i>
                                                                </div>
                                                                <div className="suggestion-content">
                                                                    <div className="suggestion-title">
                                                                        {project.title}
                                                                    </div>
                                                                    <div className="suggestion-breadcrumb">
                                                                        {project.fullPath}
                                                                    </div>
                                                                </div>
                                                                {isBookable && shortcutNumber <= 9 && (
                                                                    <div className="suggestion-shortcut">
                                                                        {shortcutNumber}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </React.Fragment>
                                        )}
                                    </React.Fragment>
                                );
                            })()}
                        </div>
                    )}

                    {/* Booking Preview */}
                    {(parsedData.duration || parsedData.project || parsedData.person || parsedData.teamMembers.length > 0) && (
                        <div className="booking-preview">
                            <div className="preview-header">
                                <i className="bi bi-eye me-2"></i>
                                <strong>Vorschau der Buchung:</strong>
                            </div>
                            <div className="preview-content">
                                {parsedData.person && parsedData.person !== UserSettings.userName && (
                                    <div className="preview-row">
                                        <span className="preview-label">Person:</span>
                                        <span className="preview-value">
                                            {Team[parsedData.person]?.realName}
                                        </span>
                                    </div>
                                )}
                                {parsedData.project && (
                                    <div className="preview-row">
                                        <span className="preview-label">Projekt:</span>
                                        <span className="preview-value">{parsedData.project.title}</span>
                                    </div>
                                )}
                                {parsedData.duration && (
                                    <div className="preview-row">
                                        <span className="preview-label">Dauer:</span>
                                        <span className="preview-value">
                                            {(() => {
                                                const multiplied = getMultipliedDuration();
                                                return `${multiplied.hours}h ${multiplied.minutes}min`;
                                            })()}
                                            {parsedData.teamMembers.length > 0 && (
                                                <span className="text-muted ms-2">
                                                    ({parsedData.duration.hours}h {parsedData.duration.minutes}min × {parsedData.teamMembers.length + 1} Personen)
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                )}
                                <div className="preview-row">
                                    <span className="preview-label">Beschreibung:</span>
                                    <span className={`preview-value ${!getFinalDescription() ? 'preview-missing' : ''}`}>
                                        {getFinalDescription() || (
                                            <>
                                                <i className="bi bi-exclamation-triangle me-1"></i>
                                                Kommentar mit Doppelpunkt verfassen
                                            </>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="quick-add-footer">
                    <button 
                        className="btn btn-secondary"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Abbrechen
                    </button>
                    <button 
                        className={`btn btn-primary ${canSubmit() ? '' : 'disabled'}`}
                        onClick={handleSubmit}
                        disabled={!canSubmit()}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Wird gebucht...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-check-lg me-2"></i>
                                Aufwand buchen
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QuickAddModal;
